<template>

    <header id="nav-header">

        <nav
            class="navbar"
            :class="{
                'navbar--internal': $route.path !== '/',
                'scrolled': isScrolled
            }"
        >

            <router-link to="/" class="navbar__logo">

                <img
                    :src="'header_logo' | staticImg"
                    :alt="'header_logo' | staticTitle"
                >

            </router-link>

            <ul class="navbar__menu">

                <li
                    v-for="(item, key) in navigation"
                    :key="key"
                    class="no-break"
                >

                    <router-link :to="item.href">
                        {{ item.label | staticContent }}
                    </router-link>

                </li>

            </ul>

            <div class="navbar__actions">

                <div class="navbar__language">

                    <a
                        href="#"
                        class="language__current uppercase"
                        @click.prevent
                    >

                        {{ currentLang | uppercase }}

                        <i class="fa-solid fa-angle-down"></i>

                    </a>

                    <ul class="language__menu">

                        <li
                            v-for="lang in langs"
                            :key="lang.key"
                        >

                            <a
                                href="#"
                                :data-lang="lang.key"
                                @click.prevent="setLang(lang.key)"
                            >
                                {{ lang.label }}
                            </a>

                        </li>

                    </ul>

                </div>

                <router-link
                    :to="{
                        name: 'Home',
                        hash: '#contact'
                    }"
                    class="navbar__connect"
                >
                    {{ 'Connect with us' | i }}
                </router-link>

                <button
                    class="menu-toggle"
                    :class="{ 'active': isMenuOpen }"
                    :aria-expanded="isMenuOpen ? 'true' : 'false'"
                    :aria-label="'nav_open_menu_label' | staticTitle"
                    @click="toggleMenu"
                >

                    <span></span>
                    <span></span>
                    <span></span>

                </button>

            </div>

        </nav>

        <div
            class="mobile-overlay"
            :class="{ 'active': isMenuOpen }"
            @click="closeMenu"
        ></div>

        <div
            class="mobile-menu"
            :class="{ 'active': isMenuOpen }"
        >

            <div class="mobile-menu__header">

                <router-link
                    to="/"
                    class="mobile-menu__logo"
                    @click.native="closeMenu"
                >

                    <img
                        :src="'header_logo' | staticImg"
                        :alt="'header_logo' | staticTitle"
                    >

                </router-link>

                <button
                    class="mobile-menu__close"
                    :aria-label="'nav_close_menu_label' | staticTitle"
                    @click="closeMenu"
                >

                    <i class="fa-solid fa-xmark"></i>

                </button>

            </div>

            <nav class="mobile-menu__nav">

                <ul>

                    <li
                        v-for="(item, key) in navigation"
                        :key="key"
                    >

                        <router-link
                            :to="item.href"
                            @click.native="closeMenu"
                        >
                            {{ item.label | staticContent }}
                        </router-link>

                    </li>

                </ul>

            </nav>

            <div class="mobile-menu__connect">

                <router-link
                    :to="{
                        name: 'Home',
                        hash: '#contact'
                    }"
                    @click.native="closeMenu"
                >
                    {{ 'Connect with us' | i }}

                    <span>↗</span>
                </router-link>

            </div>

            <div class="mobile-menu__language">

                <span>
                    {{ 'nav_language_label' | staticTitle }}
                </span>

                <a
                    href="#"
                    v-for="lang in langs"
                    :key="lang.key"
                    :data-lang="lang.key"
                    @click.prevent="setLang(lang.key)"
                >
                    {{ lang.label }}
                </a>

            </div>

        </div>

    </header>

</template>

<script>
import { langUserSet, langUserGet } from '@/utils.js';

export default {
    name: 'navHeader',

    props: {
        navigation: {
            type: Object,

            default: () => ({
                home: {
                    key: 'home',
                    label: 'nav_home',
                    href: {
                        name: 'Home',
                        hash: '#hero'
                    }
                },

                services: {
                    key: 'services',
                    label: 'nav_services',
                    href: {
                        name: 'Home',
                        hash: '#services'
                    }
                },

                packages: {
                    key: 'packages',
                    label: 'nav_packages',
                    href: {
                        name: 'Home',
                        hash: '#packages'
                    }
                },

                showroom: {
                    key: 'showroom',
                    label: 'nav_showroom',
                    href: {
                        name: 'Home',
                        hash: '#showroom'
                    }
                },

                about: {
                    key: 'about',
                    label: 'nav_about',
                    href: {
                        name: 'Home',
                        hash: '#about'
                    }
                },

                contact: {
                    key: 'contact',
                    label: 'nav_contact',
                    href: {
                        name: 'Home',
                        hash: '#contact'
                    }
                }
            })
        }
    },

    data() {
        return {
            isMenuOpen: false,
            isScrolled: false,
            currentLang: 'es',

            langs: [
                {
                    key: 'es',
                    label: 'Español'
                },
                {
                    key: 'en',
                    label: 'English'
                }
            ]
        };
    },

    created() {
        const savedLang = langUserGet();

        if (savedLang === 'en') {
            this.currentLang = 'en';
        } else {
            this.currentLang = 'es';
        }
    },

    methods: {
        setLang(lang) {
            langUserSet(lang);
            document.location.reload();
        },

        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;

            document.body.style.overflow = this.isMenuOpen
                ? 'hidden'
                : '';
        },

        closeMenu() {
            this.isMenuOpen = false;
            document.body.style.overflow = '';
        },

        handleScroll() {
            this.isScrolled = window.scrollY >= 20;
        }
    },

    mounted() {
        this.handleScroll();

        window.addEventListener(
            'scroll',
            this.handleScroll
        );
    },

    beforeDestroy() {
        window.removeEventListener(
            'scroll',
            this.handleScroll
        );

        document.body.style.overflow = '';
    }
};
</script>

<style lang="scss" scoped>
$mobile-breakpoint: 57em;

#nav-header {
    position: relative;

    z-index: 1000;
}

.navbar {
    position: fixed;

    top: 0;
    left: 0;

    z-index: 1100;

    width: 100%;
    height: 5em;

    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 0 1.25em;

    background: transparent;

    backdrop-filter: blur(0);
    -webkit-backdrop-filter: blur(0);

    border-bottom:
        0.0625em solid
        transparent;

    transition:
        background .35s ease,
        backdrop-filter .35s ease,
        border-color .35s ease,
        box-shadow .35s ease;

    &.scrolled,
    &--internal {
        background:
            rgba(1, 16, 36, .96);

        backdrop-filter:
            blur(0.875em);

        -webkit-backdrop-filter:
            blur(0.875em);

        border-bottom:
            0.0625em solid
            var(--border-1);

        box-shadow:
            0 0.5em 1.875em
            rgba(0, 0, 0, .30);
    }

    &__logo {
        display: flex;
        align-items: center;

        flex-shrink: 0;

        text-decoration: none;

        img {
            display: block;

            width: 9.375em;
            max-height: 3.375em;

            height: auto;

            object-fit: contain;
        }
    }

    &__menu {
        display: none;

        list-style: none;

        margin: 0;
        padding: 0;

        li {
            list-style: none;
        }

        a {
            position: relative;

            display: block;

            color: var(--text-1);

            font-size: 0.9375em;
            font-weight: 500;
            -letter-spacing: 0.0625em;

            text-decoration: none;

            transition:
                var(--transition);

            &::after {
                content: "";

                position: absolute;

                left: 50%;
                bottom: -0.625em;

                width: 0;
                height: 0.125em;

                background:
                    var(--color-3);

                transform:
                    translateX(-50%);

                transition:
                    var(--transition);
            }

            &:hover {
                color:
                    var(--text-3);

                &::after {
                    width: 100%;
                }
            }
        }
    }

    &__actions {
        display: flex;
        align-items: center;

        flex-shrink: 0;
    }

    &__language {
        position: relative;

        display: none;

        margin-left: 1.875em;

        .language__current {
            display: flex;
            align-items: center;

            gap: 0.5em;

            color: var(--text-1);

            font-family: var(--font-tech);
            font-size: 0.75em;
            font-weight: 600;
            -letter-spacing: 0.1em;

            text-decoration: none;

            i {
                font-size: 0.6875em;

                transition: .3s ease;
            }
        }

        .language__menu {
            position: absolute;

            top: calc(100% + 1.125em);
            right: 0;

            min-width: 10.625em;

            margin: 0;
            padding: 0.75em 0;

            list-style: none;

            background:
                rgba(1, 16, 36, .98);

            border:
                0.0625em solid
                var(--border-1);

            border-radius:
                0.875em;

            backdrop-filter:
                blur(1.25em);

            -webkit-backdrop-filter:
                blur(1.25em);

            box-shadow:
                0 1.25em 3.125em
                rgba(0, 0, 0, .30);

            opacity: 0;
            visibility: hidden;

            transform:
                translateY(0.625em);

            transition: .3s ease;

            li {
                width: 100%;
            }

            a {
                display: block;

                padding:
                    0.75em 1.375em;

                color:
                    var(--text-1);

                font-size:
                    0.9375em;

                text-decoration: none;

                transition: .3s ease;

                &:hover {
                    color:
                        var(--text-3);

                    background:
                        rgba(
                            233,
                            81,
                            29,
                            .08
                        );
                }
            }
        }

        &:hover
        .language__menu {
            opacity: 1;
            visibility: visible;

            transform:
                translateY(0);
        }

        &:hover
        .language__current i {
            transform:
                rotate(180deg);
        }
    }

    &__connect {
        display: none;

        align-items: center;
        justify-content: center;

        min-height: 2.75em;

        margin-left: 1.25em;

        padding:
            0 1.45em;

        border:
            0.0625em solid
            var(--color-3);

        background:
            var(--color-3);

        color:
            var(--text-1);

        font-family:
            var(--font-tech);

        font-size:
            0.68em;

        font-weight: 600;

        -letter-spacing:
            0.12em;

        white-space: nowrap;

        text-decoration: none;
        text-transform: uppercase;

        transition:
            background .3s ease,
            color .3s ease,
            transform .3s ease,
            box-shadow .3s ease;

        &:hover {
            background:
                transparent;

            color:
                var(--text-3);

            transform:
                translateY(-0.125em);

            box-shadow:
                0 0.5em 1.5em
                rgba(
                    233,
                    81,
                    29,
                    .14
                );
        }
    }
}

.menu-toggle {
    position: relative;

    display: flex;
    justify-content: center;
    align-items: center;

    width: 2.75em;
    height: 2.75em;

    padding: 0;

    border: none;

    background: transparent;

    cursor: pointer;

    flex-shrink: 0;

    z-index: 2100;

    span {
        position: absolute;

        width: 1.625em;
        height: 0.125em;

        background:
            var(--text-1);

        transition: .35s ease;

        pointer-events: none;

        &:nth-child(1) {
            transform:
                translateY(-0.5em);
        }

        &:nth-child(2) {
            transform:
                translateY(0);
        }

        &:nth-child(3) {
            transform:
                translateY(0.5em);
        }
    }

    &.active {
        span:nth-child(1) {
            transform:
                rotate(45deg);
        }

        span:nth-child(2) {
            opacity: 0;
        }

        span:nth-child(3) {
            transform:
                rotate(-45deg);
        }
    }
}

.mobile-overlay {
    position: fixed;

    inset: 0;

    background:
        rgba(0, 0, 0, .58);

    backdrop-filter:
        blur(0.25em);

    -webkit-backdrop-filter:
        blur(0.25em);

    opacity: 0;
    visibility: hidden;

    transition:
        .35s ease;

    z-index: 1998;

    &.active {
        opacity: 1;
        visibility: visible;
    }
}

.mobile-menu {
    position: fixed;

    top: 0;
    right: -21.25em;

    width: 20em;
    max-width: 90vw;

    height: 100vh;

    display: flex;
    flex-direction: column;

    overflow-y: auto;

    background:
        rgba(1, 16, 36, .98);

    backdrop-filter:
        blur(1.5em);

    -webkit-backdrop-filter:
        blur(1.5em);

    border-left:
        0.0625em solid
        var(--border-1);

    box-shadow:
        -0.9375em 0 3.75em
        rgba(0, 0, 0, .45);

    transition:
        right .35s ease;

    z-index: 2000;

    &.active {
        right: 0;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        padding: 1.5em;

        border-bottom:
            0.0625em solid
            var(--border-1);
    }

    &__logo img {
        display: block;

        width: 8.75em;
        max-height: 3.125em;

        object-fit: contain;
    }

    &__close {
        display: flex;
        justify-content: center;
        align-items: center;

        width: 2.5em;
        height: 2.5em;

        padding: 0;

        background:
            transparent;

        border: none;

        color:
            var(--text-1);

        font-size: 1.5em;

        cursor: pointer;

        transition:
            .35s ease;

        &:hover {
            color:
                var(--text-3);

            transform:
                rotate(90deg);
        }
    }

    &__nav {
        padding:
            2.625em 1.875em;

        ul {
            margin: 0;
            padding: 0;

            list-style: none;
        }

        li {
            margin-bottom:
                1.375em;
        }

        a {
            position: relative;

            display: inline-block;

            color:
                var(--text-1);

            font-size: 1em;
            font-weight: 500;
            -letter-spacing:
                0.125em;

            text-transform:
                uppercase;

            text-decoration: none;

            transition:
                .35s ease;

            &::after {
                content: "";

                position: absolute;

                left: 0;
                bottom: -0.5em;

                width: 0;
                height: 0.125em;

                background:
                    var(--color-3);

                transition:
                    .35s ease;
            }

            &:hover {
                color:
                    var(--text-3);

                &::after {
                    width: 100%;
                }
            }
        }
    }

    &__connect {
        padding:
            0 1.875em
            1.625em;

        a {
            display: flex;
            align-items: center;
            justify-content: space-between;

            gap: 1em;

            width: 100%;

            min-height: 3.25em;

            padding:
                0 1.25em;

            background:
                var(--color-3);

            border:
                0.0625em solid
                var(--color-3);

            color:
                var(--text-1);

            font-family:
                var(--font-tech);

            font-size:
                0.72em;

            font-weight: 600;

            -letter-spacing:
                0.12em;

            text-decoration: none;
            text-transform: uppercase;

            span {
                font-size:
                    1.35em;
            }
        }
    }

    &__language {
        margin-top: auto;

        padding:
            1.625em 1.875em;

        border-top:
            0.0625em solid
            var(--border-1);

        span {
            display: block;

            margin-bottom: 1em;

            color:
                var(--text-3);

            font-size:
                0.75em;

            font-weight: 700;

            -letter-spacing:
                0.1875em;

            text-transform:
                uppercase;
        }

        a {
            display: block;

            margin-bottom:
                0.75em;

            color:
                var(--text-2);

            text-decoration:
                none;

            transition:
                .3s ease;

            &:hover {
                color:
                    var(--text-3);

                padding-left:
                    0.5em;
            }
        }
    }
}

@media (min-width: #{$mobile-breakpoint}) {
    .navbar {
        height: 5.625em;

        padding: 0 5%;

        &__logo img {
            width: 11.25em;
            max-height: 3.75em;
        }

        &__menu {
            display: flex;

            gap: 1.4em;
        }

        &__language {
            display: block;
        }

        &__connect {
            display: inline-flex;
        }
    }

    .menu-toggle {
        display: none;
    }
}

@media (min-width: 75em) {
    .navbar {
        &__menu {
            gap: 2.2em;
        }
    }
}

@media (min-width: 87.5em) {
    .navbar {
        &__menu {
            gap: 2.625em;
        }
    }
}
</style>